import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Info, Lock, Pencil, Plus, Undo2, Unlock } from "lucide-react";
import { Button, Dialog, Field, FieldLabel, Input } from "@akuna/ui";
import { toast } from "@/hooks/use-toast";
import { useOpenCashSession, useToggleCashSession } from "@/hooks/useFinancial";
import { useProfile } from "@/hooks/useProfile";
import { activeEntries, daysFrom, parseMoney } from "@/lib/financial/calc";
import type { FinancialAccount, FinancialCashSession, FinancialCategory, FinancialEntry } from "@/lib/financial/types";
import { cn } from "@/lib/utils";
import type { FormMode } from "./FinancialFormDialog";
import { isEntryLinked, moneyTone, salesWithoutSession, thClass } from "./helpers";
import { MethodChip, Panel } from "./ui";
import { useFinancialFormat } from "./useFinancialFormat";

interface Props {
  day: string;
  today: string;
  onDay: (iso: string) => void;
  sessions: FinancialCashSession[];
  entries: FinancialEntry[];
  accounts: FinancialAccount[];
  categories: FinancialCategory[];
  canCreate: boolean;
  canUpdate: boolean;
  onForm: (mode: FormMode) => void;
}

export function CashboxTab({ day, today, onDay, sessions, entries, accounts, categories, canCreate, canUpdate, onForm }: Props) {
  const { t } = useTranslation("financial");
  const fmt = useFinancialFormat();
  const { data: profile } = useProfile();
  const operator = profile?.full_name ?? "";
  const userId = profile?.id ?? null;
  const [openDialog, setOpenDialog] = useState(false);
  const [openingAmount, setOpeningAmount] = useState("");
  const openSession = useOpenCashSession();
  const toggleSession = useToggleCashSession();

  const isToday = day === today;
  const future = daysFrom(today, day) > 0;
  const session = sessions.find((s) => s.business_date === day);
  const catById = new Map(categories.map((c) => [c.id, c]));
  const moves = session
    ? activeEntries(entries)
        .filter((e) => e.cash_session_id === session.id)
        .sort((a, b) => a.occurred_at.localeCompare(b.occurred_at))
    : [];

  const opening = Number(session?.opening_amount ?? 0);
  const inflow = moves.filter((e) => Number(e.amount) > 0).reduce((a, e) => a + Number(e.amount), 0);
  const outflow = moves.filter((e) => Number(e.amount) < 0).reduce((a, e) => a + Number(e.amount), 0);
  const drawer = opening + moves.filter((e) => e.payment_method === "cash").reduce((a, e) => a + Number(e.amount), 0);
  const byMethod = new Map<string, number>();
  for (const e of moves) if (Number(e.amount) > 0) byMethod.set(fmt.method(e.payment_method), (byMethod.get(fmt.method(e.payment_method)) ?? 0) + Number(e.amount));
  const methodRows = [...byMethod.entries()].sort((a, b) => b[1] - a[1]);

  const count = moves.length + (session ? 1 : 0);
  const status = future
    ? t("cashbox.futureStatus")
    : !session
      ? isToday ? t("cashbox.notOpened") : t("cashbox.noMovement")
      : !session.closed_at
        ? t("cashbox.openStatus", { name: session.opened_by_name ?? "—", time: fmt.time(session.opened_at), count })
        : t("cashbox.closedStatus", { name: session.closed_by_name ?? "—", count });

  const hasCashAccount = accounts.some((a) => a.kind === "cash");
  const loose = salesWithoutSession(entries, day);

  const doOpen = async () => {
    try {
      await openSession.mutateAsync({ businessDate: day, openingAmount: parseMoney(openingAmount), operatorName: operator });
      toast({ title: t("toasts.cashOpened") });
      setOpenDialog(false);
      setOpeningAmount("");
    } catch {
      toast({ title: t("toasts.error"), variant: "destructive" });
    }
  };

  const doToggle = async () => {
    if (!session) return;
    const close = !session.closed_at;
    try {
      await toggleSession.mutateAsync({ id: session.id, close, operatorName: operator });
      toast({ title: t(close ? "toasts.cashClosed" : "toasts.cashReopened") });
    } catch {
      toast({ title: t("toasts.error"), variant: "destructive" });
    }
  };

  const emptyText = future ? t("cashbox.future") : !session && !moves.length ? t("cashbox.empty") : "";

  return (
    <div className="grid grid-cols-1 items-start gap-4 xl:grid-cols-[1.6fr_1fr]">
      <Panel className="overflow-hidden">
        <div className="flex flex-col gap-3 border-b border-border px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="font-display text-lg font-bold">{isToday ? t("cashbox.todayTitle") : t("cashbox.dayTitle", { date: fmt.dayMonth(day) })}</h2>
            <p className="mt-0.5 text-sm text-muted-foreground">{status}</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {!isToday ? (
              <Button variant="outline" size="sm" onClick={() => onDay(today)}>
                <Undo2 className="mr-1.5 h-4 w-4" aria-hidden />
                {t("cashbox.backToToday")}
              </Button>
            ) : null}
            {isToday && session && !session.closed_at && canCreate ? (
              <Button variant="outline" size="sm" onClick={() => onForm({ type: "entry", cashSessionId: session.id, date: today })}>
                <Plus className="mr-1.5 h-4 w-4" aria-hidden />
                {t("cashbox.movement")}
              </Button>
            ) : null}
            {isToday && !session && canCreate ? (
              <Button size="sm" onClick={() => setOpenDialog(true)} disabled={!hasCashAccount}>
                <Unlock className="mr-1.5 h-4 w-4" aria-hidden />
                {t("cashbox.open")}
              </Button>
            ) : null}
            {/* fechar: quem edita o caixa ou quem abriu; reabrir: só quem edita */}
            {isToday && session && (canUpdate || (!session.closed_at && !!userId && session.opened_by === userId)) ? (
              <Button size="sm" variant={session.closed_at ? "outline" : "default"} onClick={doToggle} loading={toggleSession.isPending}>
                {session.closed_at ? <Undo2 className="mr-1.5 h-4 w-4" aria-hidden /> : <Lock className="mr-1.5 h-4 w-4" aria-hidden />}
                {session.closed_at ? t("cashbox.reopen") : t("cashbox.close")}
              </Button>
            ) : null}
          </div>
        </div>
        {!hasCashAccount ? <p className="px-5 py-3 text-sm text-muted-foreground">{t("cashbox.noCashAccount")}</p> : null}
        {loose.count > 0 ? (
          <p role="status" className="border-b border-border bg-state-warning-soft px-5 py-3 text-sm text-foreground">
            {t("cashbox.salesWithoutSession", { count: loose.count, total: fmt.brl(loose.total) })}
          </p>
        ) : null}
        <div className="overflow-x-auto">
          <table className="w-full min-w-[620px] text-sm">
            <thead>
              <tr className="bg-muted/40">
                <th className={cn(thClass, "w-20 pl-5")}>{t("cashbox.time")}</th>
                <th className={thClass}>{t("cashbox.what")}</th>
                <th className={cn(thClass, "w-40")}>{t("cashbox.method")}</th>
                <th className={cn(thClass, "w-36 text-right")}>{t("cashbox.amount")}</th>
                <th className={cn(thClass, "w-14 pr-5")}><span className="sr-only">{t("cashbox.fix")}</span></th>
              </tr>
            </thead>
            <tbody>
              {session ? (
                <tr className="border-t border-border/70">
                  <td className="py-2.5 pl-5 pr-3 tabular-nums text-muted-foreground">{fmt.time(session.opened_at)}</td>
                  <td className="px-3 py-2.5">
                    <div className="font-semibold">{t("cashbox.opening")}</div>
                    <div className="mt-0.5 text-xs text-muted-foreground">{t("cashbox.openingSub", { name: session.opened_by_name ?? "—" })}</div>
                  </td>
                  <td className="px-3 py-2.5"><MethodChip>{t("methods.cash")}</MethodChip></td>
                  <td className="px-3 py-2.5 text-right font-display text-[15px] font-bold text-state-success-foreground">{fmt.signed(opening)}</td>
                  <td className="py-2.5 pl-2 pr-5" />
                </tr>
              ) : null}
              {moves.map((e) => (
                <tr key={e.id} className="border-t border-border/70">
                  <td className="py-2.5 pl-5 pr-3 tabular-nums text-muted-foreground">{fmt.time(e.occurred_at)}</td>
                  <td className="px-3 py-2.5">
                    <div className="font-semibold">{e.description || fmt.category(catById.get(e.category_id ?? ""))}</div>
                    <div className="mt-0.5 text-xs text-muted-foreground">{e.detail ?? fmt.category(catById.get(e.category_id ?? ""))}</div>
                  </td>
                  <td className="px-3 py-2.5"><MethodChip>{fmt.method(e.payment_method)}</MethodChip></td>
                  <td className={cn("px-3 py-2.5 text-right font-display text-[15px] font-bold", moneyTone(Number(e.amount)))}>{fmt.signed(Number(e.amount))}</td>
                  <td className="py-2.5 pl-2 pr-5 text-right">
                    {canUpdate && !isEntryLinked(e) ? (
                      <Button size="icon-sm" variant="outline" aria-label={t("cashbox.fix")} onClick={() => onForm({ type: "editEntry", entry: e })}>
                        <Pencil className="h-3.5 w-3.5" aria-hidden />
                      </Button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {emptyText ? <p className="px-5 py-7 text-center text-sm text-muted-foreground">{emptyText}</p> : null}
      </Panel>

      <div className="flex flex-col gap-4">
        <div className="rounded-2xl bg-state-info p-5 text-primary-foreground">
          <div className="font-display text-[11px] font-semibold uppercase tracking-[0.14em] opacity-80">{isToday ? t("cashbox.drawerNow") : t("cashbox.drawerEnd")}</div>
          <div className="mt-2.5 font-display text-3xl font-extrabold tracking-tight">{fmt.brl(drawer)}</div>
          <div className="mt-1 text-sm opacity-90">{isToday ? t("cashbox.drawerNowSub") : t("cashbox.drawerEndSub")}</div>
        </div>

        <Panel className="p-5">
          <div className="font-display text-base font-bold">{isToday ? t("cashbox.byMethodToday") : t("cashbox.byMethodDay")}</div>
          <div className="mt-3 flex flex-col">
            {methodRows.map(([name, v]) => (
              <div key={name} className="flex items-center justify-between border-b border-border/60 py-2.5">
                <span className="text-sm text-foreground/80">{name}</span>
                <span className="font-display text-[15px] font-bold">{fmt.brl(v)}</span>
              </div>
            ))}
          </div>
          <div className="mt-3 flex items-center justify-between border-t-2 border-border pt-3">
            <span className="text-sm font-semibold">{t("cashbox.totalIn")}</span>
            <span className="font-display text-lg font-extrabold text-state-success-foreground">{fmt.brl(inflow)}</span>
          </div>
          <div className="mt-2 flex items-center justify-between">
            <span className="text-sm text-muted-foreground">{t("cashbox.totalOut")}</span>
            <span className="font-display text-[15px] font-bold text-state-danger-foreground">{fmt.brl(Math.abs(outflow))}</span>
          </div>
        </Panel>
      </div>

      {openDialog ? (
        <Dialog
          open
          onOpenChange={(o) => !o && setOpenDialog(false)}
          title={t("cashbox.openDialogTitle")}
          closeLabel={t("form.cancel")}
          footer={
            <div className="flex w-full justify-end gap-2">
              <Button variant="outline" onClick={() => setOpenDialog(false)}>{t("form.cancel")}</Button>
              <Button onClick={doOpen} loading={openSession.isPending}>{t("cashbox.open")}</Button>
            </div>
          }
        >
          <div className="grid gap-3.5">
            <Field>
              <FieldLabel>{t("cashbox.openingAmount")}</FieldLabel>
              <Input inputMode="decimal" placeholder="0,00" value={openingAmount} onChange={(e) => setOpeningAmount(e.target.value)} />
            </Field>
            <Field>
              <FieldLabel>{t("cashbox.operator")}</FieldLabel>
              <Input value={operator} readOnly />
            </Field>
          </div>
        </Dialog>
      ) : null}
    </div>
  );
}
